import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  getTaxFilingUploadEmailSettings,
  updateTaxFilingUploadEmailSettings,
} from '@/services/taxFilingsApi';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

export default function AdminSettingsPage() {
  const [enabled, setEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const settings = await getTaxFilingUploadEmailSettings();
        setEnabled(settings.enabled);
      } catch {
        toast.error('Failed to load settings');
      } finally {
        setIsLoading(false);
      }
    }
    void load();
  }, []);

  const handleToggle = async (checked: boolean) => {
    setIsSaving(true);
    const previous = enabled;
    setEnabled(checked);
    try {
      const result = await updateTaxFilingUploadEmailSettings(checked);
      setEnabled(result.enabled);
      toast.success(
        result.enabled
          ? 'Tax filing upload emails enabled'
          : 'Tax filing upload emails disabled'
      );
    } catch {
      setEnabled(previous);
      toast.error('Failed to update settings');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-4 w-96" />
        </div>
        <Skeleton className="h-40" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground mt-1">
          Configure tax filing notification preferences.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Tax filing upload emails</CardTitle>
          <CardDescription>
            When enabled, clients receive an email when a tax filing document is
            uploaded for the current default tax year only. Uploads for other tax
            years do not trigger email notifications.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-3">
            <Checkbox
              id="tax-filing-upload-emails"
              checked={enabled}
              disabled={isSaving}
              onCheckedChange={(value) => {
                if (value === 'indeterminate') return;
                void handleToggle(value);
              }}
            />
            <div className="space-y-1">
              <Label htmlFor="tax-filing-upload-emails" className="cursor-pointer">
                Send email when tax filing documents are uploaded
              </Label>
              <p className="text-sm text-muted-foreground">
                Emails are sent only for the platform default tax year when this
                setting is on.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
